import {
  RfcAchForm,
  RfcClientPortalAccessForm,
  RfcMecForm,
  RfcSection125Form,
  RfcInternalData,
} from '@common/lib/rfcSchema';
import {Button} from '@common-web/components/ui/button';
import {createCsv} from '@common-web/lib/createCsv';
import {reportError} from '@common-web/lib/errorReporting';
import {FileDown} from 'lucide-react';
import {useState} from 'react';

type RfcCsvProps = {
  clientName: string;
  section125FormEnabled: boolean;
  mecFormEnabled: boolean;
  section125Form: RfcSection125Form;
  mecForm: RfcMecForm;
  achForm?: RfcAchForm;
  clientPortalAccessForm: RfcClientPortalAccessForm;
  internalData?: RfcInternalData;
  includeInternalFields: boolean;
  showPepm: boolean;
};

function displayValue(value: unknown): string {
  if (value === undefined || value === null || value === '') {
    return '—';
  }
  if (Array.isArray(value)) {
    return value.map((v) => displayValue(v)).join(', ');
  }
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }
  return String(value);
}

export function RfcGenerateCsvButton(props: RfcCsvProps) {
  const [loadingCsv, setLoadingCsv] = useState(false);

  async function handleGenerateCsv() {
    try {
      setLoadingCsv(true);
      const rows: [string, string][] = [];

      const s125 = props.section125FormEnabled ? props.section125Form : undefined;
      const mec = props.mecFormEnabled ? props.mecForm : undefined;

      function pushAchRows(achForm: RfcAchForm, prefix: string) {
        if (!achForm.achDetails?.bankName && !achForm.achDetails?.routingNumber) return;
        rows.push([
          `${prefix} – ACH Business Name on Account`,
          displayValue(achForm.bankAccount?.businessNameOnAccount),
        ]);
        rows.push([
          `${prefix} – ACH Account Holder First Name`,
          displayValue(achForm.bankAccount?.accountHolderFirstName),
        ]);
        rows.push([
          `${prefix} – ACH Account Holder Last Name`,
          displayValue(achForm.bankAccount?.accountHolderLastName),
        ]);
        rows.push([
          `${prefix} – ACH Contact Phone`,
          displayValue(achForm.bankAccount?.contactPhone),
        ]);
        rows.push([
          `${prefix} – ACH Contact Email`,
          displayValue(achForm.bankAccount?.contactEmail),
        ]);
        rows.push([
          `${prefix} – ACH Account Address Street`,
          displayValue(achForm.bankAccount?.address?.street),
        ]);
        rows.push([
          `${prefix} – ACH Account Address City/State/ZIP`,
          displayValue(
            [
              achForm.bankAccount?.address?.city,
              achForm.bankAccount?.address?.state,
              achForm.bankAccount?.address?.zip,
            ]
              .filter(Boolean)
              .join(', '),
          ),
        ]);
        rows.push([`${prefix} – ACH Bank Name`, displayValue(achForm.achDetails?.bankName)]);
        rows.push([
          `${prefix} – ACH Routing Number`,
          displayValue(achForm.achDetails?.routingNumber),
        ]);
        rows.push([
          `${prefix} – ACH Account Number`,
          displayValue(achForm.achDetails?.accountNumber),
        ]);
        rows.push([
          `${prefix} – ACH Bank Address Street`,
          displayValue(achForm.achDetails?.bankAddress?.street),
        ]);
        rows.push([
          `${prefix} – ACH Bank Address City/State/ZIP`,
          displayValue(
            [
              achForm.achDetails?.bankAddress?.city,
              achForm.achDetails?.bankAddress?.state,
              achForm.achDetails?.bankAddress?.zip,
            ]
              .filter(Boolean)
              .join(', '),
          ),
        ]);
        rows.push([
          `${prefix} – ACH Authorized Signature`,
          displayValue(achForm.authorization?.authorizedSignature),
        ]);
        rows.push([
          `${prefix} – ACH Signature Date`,
          displayValue(achForm.authorization?.signatureDate),
        ]);
      }

      if (props.includeInternalFields) {
        rows.push(['Client', props.clientName || '—']);
      }
      if (s125) {
        rows.push(['', '']); // spacer
        rows.push(['125 Program – Group Information', '']);
        rows.push(['Benefit Year', displayValue(s125.groupInfo?.benefitYear)]);
        rows.push(['Company Legal Name', displayValue(s125.groupInfo?.companyName)]);
        rows.push(['Company DBA', displayValue(s125.groupInfo?.companyDba)]);
        rows.push(['Other Known Name', displayValue(s125.groupInfo?.otherKnownName)]);
        rows.push(['SIC Code', displayValue(s125.groupInfo?.sic)]);
        rows.push([
          'Primary State of Doing Business',
          displayValue(s125.groupInfo?.stateOfDoingBusiness),
        ]);
        rows.push(['Total # of Employees', displayValue(s125.groupInfo?.totalEmployees)]);
        rows.push(['Number of Locations', displayValue(s125.groupInfo?.numberOfLocations)]);
        if (s125.groupInfo?.locations?.length) {
          s125.groupInfo.locations.forEach((loc, idx) => {
            const prefix = `Location ${idx + 1}`;
            rows.push(['', '']);
            rows.push([`125 Program – ${prefix}`, '']);
            rows.push([`${prefix} – Name`, displayValue(loc.locationName)]);
            rows.push([`${prefix} – FEIN`, displayValue(loc.fein)]);
            rows.push([`${prefix} – Payroll Location Code`, displayValue(loc.payrollLocationCode)]);
            rows.push([
              `${prefix} – Company Address Street`,
              displayValue(loc.companyAddress?.street),
            ]);
            rows.push([
              `${prefix} – Company Address City/State/ZIP`,
              displayValue(
                [loc.companyAddress?.city, loc.companyAddress?.state, loc.companyAddress?.zip]
                  .filter(Boolean)
                  .join(', '),
              ),
            ]);
            rows.push([
              `${prefix} – Mailing Address Street`,
              displayValue(loc.mailingAddress?.street),
            ]);
            rows.push([
              `${prefix} – Mailing Address City/State/ZIP`,
              displayValue(
                [loc.mailingAddress?.city, loc.mailingAddress?.state, loc.mailingAddress?.zip]
                  .filter(Boolean)
                  .join(', '),
              ),
            ]);
            rows.push([
              `${prefix} – Contact First Name`,
              displayValue(loc.contactPerson?.firstName),
            ]);
            rows.push([`${prefix} – Contact Last Name`, displayValue(loc.contactPerson?.lastName)]);
            rows.push([`${prefix} – Contact Title`, displayValue(loc.contactPerson?.title)]);
            rows.push([`${prefix} – Contact Phone`, displayValue(loc.contactPerson?.phone)]);
            rows.push([`${prefix} – Contact Email`, displayValue(loc.contactPerson?.email)]);
            if (loc.achForm) {
              pushAchRows(loc.achForm, prefix);
            }
          });
        }

        rows.push(['', '']);
        rows.push(['125 Program – Eligibility & Admin', '']);
        rows.push(['Full Time Eligible', displayValue(s125.admin?.eligibility?.fullTime)]);
        rows.push(['Part Time Eligible', displayValue(s125.admin?.eligibility?.partTime)]);
        rows.push([
          'Waiting Period for Newly Hired Employees (days)',
          displayValue(s125.admin?.waitingPeriod),
        ]);
        rows.push([
          'Payroll Frequencies',
          displayValue(
            s125.admin?.payrollFrequencies?.map((x) => x.replace(/_/g, ' ').toLowerCase()),
          ),
        ]);
        rows.push(['Enrollment Company', displayValue(s125.admin?.enrollmentCompany)]);
        rows.push([
          'Enrollment Type',
          displayValue(s125.admin?.enrollmentType?.replace(/_/g, ' ').toLowerCase()),
        ]);

        rows.push(['', '']);
        rows.push(['125 Program – Contacts', '']);
        rows.push([
          'Authorized Signer – First Name',
          displayValue(s125.contacts?.authorizedSigner?.firstName),
        ]);
        rows.push([
          'Authorized Signer – Last Name',
          displayValue(s125.contacts?.authorizedSigner?.lastName),
        ]);
        rows.push([
          'Authorized Signer – Title',
          displayValue(s125.contacts?.authorizedSigner?.title),
        ]);
        rows.push([
          'Authorized Signer – Phone',
          displayValue(s125.contacts?.authorizedSigner?.phone),
        ]);
        rows.push([
          'Authorized Signer – Email',
          displayValue(s125.contacts?.authorizedSigner?.email),
        ]);
        rows.push([
          'Eligibility Contact – First Name',
          displayValue(s125.contacts?.eligibilityContact?.firstName),
        ]);
        rows.push([
          'Eligibility Contact – Last Name',
          displayValue(s125.contacts?.eligibilityContact?.lastName),
        ]);
        rows.push([
          'Eligibility Contact – Title',
          displayValue(s125.contacts?.eligibilityContact?.title),
        ]);
        rows.push([
          'Eligibility Contact – Phone',
          displayValue(s125.contacts?.eligibilityContact?.phone),
        ]);
        rows.push([
          'Eligibility Contact – Email',
          displayValue(s125.contacts?.eligibilityContact?.email),
        ]);
        rows.push([
          'Billing Contact – First Name',
          displayValue(s125.contacts?.billingContact?.firstName),
        ]);
        rows.push([
          'Billing Contact – Last Name',
          displayValue(s125.contacts?.billingContact?.lastName),
        ]);
        rows.push(['Billing Contact – Title', displayValue(s125.contacts?.billingContact?.title)]);
        rows.push(['Billing Contact – Phone', displayValue(s125.contacts?.billingContact?.phone)]);
        rows.push(['Billing Contact – Email', displayValue(s125.contacts?.billingContact?.email)]);

        rows.push(['', '']);
        rows.push(['125 Program – Payroll', '']);
        rows.push([
          'Internal Payroll – First Name',
          displayValue(s125.payrollInfo?.contact?.firstName),
        ]);
        rows.push([
          'Internal Payroll – Last Name',
          displayValue(s125.payrollInfo?.contact?.lastName),
        ]);
        rows.push(['Internal Payroll – Title', displayValue(s125.payrollInfo?.contact?.title)]);
        rows.push(['Internal Payroll – Phone', displayValue(s125.payrollInfo?.contact?.phone)]);
        rows.push(['Internal Payroll – Email', displayValue(s125.payrollInfo?.contact?.email)]);
        rows.push([
          'Payroll Vendor Contact – First Name',
          displayValue(s125.payrollInfo?.payrollVendorContact?.firstName),
        ]);
        rows.push([
          'Payroll Vendor Contact – Last Name',
          displayValue(s125.payrollInfo?.payrollVendorContact?.lastName),
        ]);
        rows.push([
          'Payroll Vendor Contact – Title',
          displayValue(s125.payrollInfo?.payrollVendorContact?.title),
        ]);
        rows.push([
          'Payroll Vendor Contact – Phone',
          displayValue(s125.payrollInfo?.payrollVendorContact?.phone),
        ]);
        rows.push([
          'Payroll Vendor Contact – Email',
          displayValue(s125.payrollInfo?.payrollVendorContact?.email),
        ]);
        rows.push(['Payroll Vendor', displayValue(s125.payrollInfo?.vendor)]);
        rows.push(['Payroll System', displayValue(s125.payrollInfo?.system)]);
        rows.push(['Payroll Company ID', displayValue(s125.payrollInfo?.payrollCompanyId)]);

        rows.push(['', '']);
        rows.push(['125 Program – Agent of Record', '']);
        rows.push(['First Name', displayValue(s125.agentOfRecord?.firstName)]);
        rows.push(['Last Name', displayValue(s125.agentOfRecord?.lastName)]);
        rows.push(['Phone', displayValue(s125.agentOfRecord?.phone)]);
        rows.push(['Email', displayValue(s125.agentOfRecord?.email)]);
        rows.push(['Additional Phone', displayValue(s125.agentOfRecord?.additionalPhone)]);
        rows.push(['Additional Email', displayValue(s125.agentOfRecord?.additionalEmail)]);
        if (props.showPepm) {
          rows.push(['PEPM', displayValue(s125.agentOfRecord?.pepm)]);
        }
      }

      if (mec) {
        rows.push(['', '']);
        rows.push(['MEC Plan – Group Information', '']);
        rows.push(['Benefit Year', displayValue(mec.groupInfo?.benefitYear)]);
        rows.push(['Company Legal Name', displayValue(mec.groupInfo?.companyName)]);
        rows.push(['Company DBA', displayValue(mec.groupInfo?.companyDba)]);
        rows.push(['Other Known Name', displayValue(mec.groupInfo?.otherKnownName)]);
        rows.push(['SIC Code', displayValue(mec.groupInfo?.sic)]);
        rows.push([
          'Primary State of Doing Business',
          displayValue(mec.groupInfo?.stateOfDoingBusiness),
        ]);
        rows.push(['Total # of Employees', displayValue(mec.groupInfo?.totalEmployees)]);
        rows.push(['Number of Locations', displayValue(mec.groupInfo?.numberOfLocations)]);
        if (mec.groupInfo?.locations?.length) {
          mec.groupInfo.locations.forEach((loc, idx) => {
            const prefix = `Location ${idx + 1}`;
            rows.push(['', '']);
            rows.push([`MEC Plan – ${prefix}`, '']);
            rows.push([`${prefix} – Name`, displayValue(loc.locationName)]);
            rows.push([`${prefix} – FEIN`, displayValue(loc.fein)]);
            rows.push([`${prefix} – Payroll Location Code`, displayValue(loc.payrollLocationCode)]);
            rows.push([
              `${prefix} – Company Address Street`,
              displayValue(loc.companyAddress?.street),
            ]);
            rows.push([
              `${prefix} – Company Address City/State/ZIP`,
              displayValue(
                [loc.companyAddress?.city, loc.companyAddress?.state, loc.companyAddress?.zip]
                  .filter(Boolean)
                  .join(', '),
              ),
            ]);
            rows.push([
              `${prefix} – Mailing Address Street`,
              displayValue(loc.mailingAddress?.street),
            ]);
            rows.push([
              `${prefix} – Mailing Address City/State/ZIP`,
              displayValue(
                [loc.mailingAddress?.city, loc.mailingAddress?.state, loc.mailingAddress?.zip]
                  .filter(Boolean)
                  .join(', '),
              ),
            ]);
            rows.push([
              `${prefix} – Contact First Name`,
              displayValue(loc.contactPerson?.firstName),
            ]);
            rows.push([`${prefix} – Contact Last Name`, displayValue(loc.contactPerson?.lastName)]);
            rows.push([`${prefix} – Contact Title`, displayValue(loc.contactPerson?.title)]);
            rows.push([`${prefix} – Contact Phone`, displayValue(loc.contactPerson?.phone)]);
            rows.push([`${prefix} – Contact Email`, displayValue(loc.contactPerson?.email)]);
            if (loc.achForm) {
              pushAchRows(loc.achForm, prefix);
            }
          });
        }

        rows.push(['', '']);
        rows.push(['MEC Plan – Eligibility & Admin', '']);
        rows.push(['Full Time Eligible', displayValue(mec.admin?.eligibility?.fullTime)]);
        rows.push(['Part Time Eligible', displayValue(mec.admin?.eligibility?.partTime)]);
        rows.push([
          'Waiting Period for Newly Hired Employees (days)',
          displayValue(mec.admin?.waitingPeriod),
        ]);
        rows.push([
          'Payroll Frequencies',
          displayValue(
            mec.admin?.payrollFrequencies?.map((x) => x.replace(/_/g, ' ').toLowerCase()),
          ),
        ]);
        rows.push(['Enrollment Company', displayValue(mec.admin?.enrollmentCompany)]);
        rows.push([
          'Enrollment Type',
          displayValue(mec.admin?.enrollmentType?.replace(/_/g, ' ').toLowerCase()),
        ]);

        rows.push(['', '']);
        rows.push(['MEC Plan – Contacts', '']);
        rows.push([
          'Authorized Signer – First Name',
          displayValue(mec.contacts?.authorizedSigner?.firstName),
        ]);
        rows.push([
          'Authorized Signer – Last Name',
          displayValue(mec.contacts?.authorizedSigner?.lastName),
        ]);
        rows.push([
          'Authorized Signer – Title',
          displayValue(mec.contacts?.authorizedSigner?.title),
        ]);
        rows.push([
          'Authorized Signer – Phone',
          displayValue(mec.contacts?.authorizedSigner?.phone),
        ]);
        rows.push([
          'Authorized Signer – Email',
          displayValue(mec.contacts?.authorizedSigner?.email),
        ]);
        rows.push([
          'Eligibility Contact – First Name',
          displayValue(mec.contacts?.eligibilityContact?.firstName),
        ]);
        rows.push([
          'Eligibility Contact – Last Name',
          displayValue(mec.contacts?.eligibilityContact?.lastName),
        ]);
        rows.push([
          'Eligibility Contact – Title',
          displayValue(mec.contacts?.eligibilityContact?.title),
        ]);
        rows.push([
          'Eligibility Contact – Phone',
          displayValue(mec.contacts?.eligibilityContact?.phone),
        ]);
        rows.push([
          'Eligibility Contact – Email',
          displayValue(mec.contacts?.eligibilityContact?.email),
        ]);
        rows.push([
          'Billing Contact – First Name',
          displayValue(mec.contacts?.billingContact?.firstName),
        ]);
        rows.push([
          'Billing Contact – Last Name',
          displayValue(mec.contacts?.billingContact?.lastName),
        ]);
        rows.push(['Billing Contact – Title', displayValue(mec.contacts?.billingContact?.title)]);
        rows.push(['Billing Contact – Phone', displayValue(mec.contacts?.billingContact?.phone)]);
        rows.push(['Billing Contact – Email', displayValue(mec.contacts?.billingContact?.email)]);

        rows.push(['', '']);
        rows.push(['MEC Plan – Payroll', '']);
        rows.push([
          'Internal Payroll – First Name',
          displayValue(mec.payrollInfo?.contact?.firstName),
        ]);
        rows.push([
          'Internal Payroll – Last Name',
          displayValue(mec.payrollInfo?.contact?.lastName),
        ]);
        rows.push(['Internal Payroll – Phone', displayValue(mec.payrollInfo?.contact?.phone)]);
        rows.push(['Internal Payroll – Email', displayValue(mec.payrollInfo?.contact?.email)]);
        rows.push(['Payroll Vendor', displayValue(mec.payrollInfo?.vendor)]);
        rows.push([
          'Payroll Vendor Contact – First Name',
          displayValue(mec.payrollInfo?.payrollVendorContact?.firstName),
        ]);
        rows.push([
          'Payroll Vendor Contact – Last Name',
          displayValue(mec.payrollInfo?.payrollVendorContact?.lastName),
        ]);
        rows.push([
          'Payroll Vendor Contact – Phone',
          displayValue(mec.payrollInfo?.payrollVendorContact?.phone),
        ]);
        rows.push([
          'Payroll Vendor Contact – Email',
          displayValue(mec.payrollInfo?.payrollVendorContact?.email),
        ]);
        rows.push(['Payroll System', displayValue(mec.payrollInfo?.system)]);
        rows.push(['Payroll Company ID', displayValue(mec.payrollInfo?.payrollCompanyId)]);

        rows.push(['', '']);
        rows.push(['MEC Plan – Agent of Record', '']);
        rows.push(['First Name', displayValue(mec.agentOfRecord?.firstName)]);
        rows.push(['Last Name', displayValue(mec.agentOfRecord?.lastName)]);
        rows.push(['Phone', displayValue(mec.agentOfRecord?.phone)]);
        rows.push(['Email', displayValue(mec.agentOfRecord?.email)]);
        rows.push(['Additional Phone', displayValue(mec.agentOfRecord?.additionalPhone)]);
        rows.push(['Additional Email', displayValue(mec.agentOfRecord?.additionalEmail)]);
        if (props.showPepm) {
          rows.push(['PEPM', displayValue(mec.agentOfRecord?.pepm)]);
        }
      }

      if (props.clientPortalAccessForm?.users?.length) {
        rows.push(['', '']);
        rows.push(['Client Portal Access – Users', '']);
        props.clientPortalAccessForm.users.forEach((user, idx) => {
          const prefix = `User ${idx + 1}`;
          rows.push([`${prefix} – First Name`, displayValue(user.firstName)]);
          rows.push([`${prefix} – Last Name`, displayValue(user.lastName)]);
          rows.push([`${prefix} – Title`, displayValue(user.title)]);
          rows.push([`${prefix} – Phone`, displayValue(user.phone)]);
          rows.push([`${prefix} – Email`, displayValue(user.email)]);
        });
      }

      if (props.includeInternalFields && props.internalData) {
        const idata = props.internalData;
        rows.push(['', '']);
        rows.push(['Internal Configuration', '']);
        rows.push(['Group Number', displayValue(idata.groupNumber)]);
        rows.push([
          'Product Codes',
          idata.productCodes && idata.productCodes.length > 0
            ? idata.productCodes.map((pc) => pc.value).join(', ')
            : '—',
        ]);
        rows.push(['Carrier', displayValue(idata.carrierName?.replace(/_/g, ' '))]);
        if (idata.carrierName === 'OTHER')
          rows.push(['Carrier (Other)', displayValue(idata.carrierNameOther)]);
        rows.push(['Telemedicine', displayValue(idata.telemedicine?.replace(/_/g, ' '))]);
        if (idata.telemedicine === 'OTHER')
          rows.push(['Telemedicine (Other)', displayValue(idata.telemedicineOther)]);
        rows.push(['PBM', displayValue(idata.pbm?.replace(/_/g, ' '))]);
        if (idata.pbm === 'OTHER') rows.push(['PBM (Other)', displayValue(idata.pbmOther)]);
        rows.push(['App', displayValue(idata.app?.replace(/_/g, ' '))]);
        if (idata.app === 'OTHER') rows.push(['App (Other)', displayValue(idata.appOther)]);
        rows.push(['MEC', displayValue(idata.mec?.replace(/_/g, ' '))]);
        if (idata.mec === 'OTHER') rows.push(['MEC (Other)', displayValue(idata.mecOther)]);
        rows.push(['PPO Network', displayValue(idata.ppoNetwork?.replace(/_/g, ' '))]);
        if (idata.ppoNetwork === 'OTHER')
          rows.push(['PPO Network (Other)', displayValue(idata.ppoNetworkOther)]);
      }

      const csv = createCsv(rows.map(([key, value]) => ({Field: key, Value: value})));
      const blob = new Blob([csv], {type: 'text/csv;charset=utf-8;'});
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute(
        'download',
        `request-for-coverage-${(s125?.groupInfo.companyName || mec?.groupInfo.companyName || props.clientName || 'submission').toLowerCase().slice(0, 50).replace(/\s+/g, '-')}.csv`,
      );
      document.body.appendChild(link);
      link.click();
      link.parentNode!.removeChild(link);
    } catch (e) {
      reportError(e, {
        title: 'Could not generate CSV',
        fallbackMessage: 'Failed to generate the CSV. Please try again.',
        context: 'RfcCsvExport.handleGenerateCsv',
      });
    } finally {
      setLoadingCsv(false);
    }
  }

  return (
    <Button variant='outlineDark' onClick={handleGenerateCsv} disabled={loadingCsv}>
      <FileDown className='h-4 w-4 mr-2' />
      {loadingCsv ? 'Generating…' : 'Export CSV'}
    </Button>
  );
}
